/**
 * Entry Page (Service Selection)
 * @description 서비스 선택 화면 - 백엔드 API 기반 동적 렌더링
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, navigateExternal, useLocation } from '@shared/router';
import { getApiEndpoint, getServiceUrl } from '@shared/config';


import { SearchProvider, useSearch } from '@/contexts/SearchContext';
import { useAuthStore } from '@/stores/authStore';
import { useServiceStore } from '@/stores/serviceStore';
import { checkPermissionBatch } from '@/services/authApi';
import { ServiceCard, ServiceCardData, HealthStatus, ServiceHealth } from '../components/ServiceCard';
import { Building2, HardDrive, Package, Server, Shield, UserCog } from 'lucide-react';
import { getIconComponent } from '@/utils/iconMap';
import {
  usePortalTranslation,
  resolveServiceDescription,
  resolveServiceName,
} from '@shared/i18n';

interface ServiceConfig {
  folder: string;
  name: string;
  description: string;
  labels: string[];
  icon: string;
  ports: {
    frontend: number;
    backend: number;
    'frontend-admin'?: number;
  };
  adminRoute?: string;
}

// 데모용 랜덤 health 생성 (대부분 healthy, 빨간 1개, 노란 2개)
let unhealthyCount = 0;
let degradedCount = 0;
const generateHealth = (): ServiceHealth => {
  const statuses: HealthStatus[] = ['healthy', 'healthy', 'healthy'];
  
  // 랜덤하게 하나의 상태를 변경
  if (unhealthyCount < 1 && Math.random() < 0.1) {
    statuses[Math.floor(Math.random() * 3)] = 'unhealthy';
    unhealthyCount++;
  } else if (degradedCount < 2 && Math.random() < 0.15) {
    statuses[Math.floor(Math.random() * 3)] = 'unknown';
    degradedCount++;
  }
  
  return {
    frontend: statuses[0],
    frontendAdmin: statuses[1],
    backend: statuses[2],
  };
};

// Icon render helper for entry page cards with enhanced styling (20% smaller)
const renderIcon = (iconName: string | undefined): React.ReactNode => {
  const IconComponent = getIconComponent(iconName, Shield);
  return (
    <IconComponent className="w-12 h-12 text-primary [filter:drop-shadow(0_4px_6px_rgba(59,130,246,0.3))]" />
  );
};

const EntryPageContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchQuery, setOnSearchSubmit } = (useSearch() as any);
  const { user, isAuthenticated } = useAuthStore();
  const { setAccessibleServices } = useServiceStore();
  const { t: tServices, i18n } = usePortalTranslation('services');
  const { t: tLayout } = usePortalTranslation('layout');
  const [allServices, setAllServices] = useState<ServiceCardData[]>([]);
  const [serviceConfigs, setServiceConfigs] = useState<ServiceConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [servicesLoadError, setServicesLoadError] = useState<string | null>(null);
  const [menuRefreshTick, setMenuRefreshTick] = useState(0);

  // 메뉴관리 저장 후 Entry로 돌아오거나 탭 포커스 시 가시성 재평가
  useEffect(() => {
    const bump = () => setMenuRefreshTick((n) => n + 1);
    const onVis = () => {
      if (document.visibilityState === 'visible') bump();
    };
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // 서비스 목록 로드 (1회만)
  useEffect(() => {
    const loadServices = async () => {
      setServicesLoadError(null);
      try {
        const response = await fetch(getApiEndpoint('/services'));
        if (!response.ok) throw new Error('Failed to load services');
        const data = await response.json();
        setServiceConfigs(data.services || []);
      } catch (error) {
        const isNetworkError = error instanceof TypeError && (error.message === 'Failed to fetch' || (error as Error).message?.includes('fetch'));
        setServiceConfigs([]);
        if (isNetworkError) {
          setServicesLoadError(tLayout('networkError'));
        } else {
          setServicesLoadError(tLayout('entry.loadError'));
        }
        setIsLoading(false);
      }
    };
    loadServices();
  }, []);

  // 서비스 카드 생성 + 권한 체크 (user 변경 시)
  useEffect(() => {
    if (serviceConfigs.length === 0 && !user) return;

    const processServices = async () => {
      setIsLoading(true);

      // Portal 관리자인 경우
      if (user?.userType === 'portal-admin') {
        const adminServices: ServiceCardData[] = [
          {
            id: 'admin-operator',
            label: '운영자 관리',
            description: 'Portal 운영자 계정 관리',
            icon: <UserCog className="w-12 h-12 text-primary [filter:drop-shadow(0_4px_6px_rgba(168,85,247,0.3))]" />,
            href: '/admin/operator',
            labelType: ['System'],
            hasAccess: true,
            hasAdminAccess: true,
            isLoading: false,
            health: generateHealth(),
            isServiceFolder: false, // System 서비스
          },
          {
            id: 'admin-service',
            label: 'IT 서비스 관리',
            description: 'IT 서비스 요청 관리',
            icon: <Package className="w-12 h-12 text-primary [filter:drop-shadow(0_4px_6px_rgba(34,197,94,0.3))]" />,
            href: '/admin/service',
            labelType: ['System'],
            hasAccess: true,
            hasAdminAccess: true,
            isLoading: false,
            health: generateHealth(),
            isServiceFolder: false, // System 서비스
          },
          {
            id: 'admin-portal-service',
            label: 'Portal 서비스 관리',
            description: 'Portal 서비스 설정 및 관리',
            icon: <Server className="w-12 h-12 text-primary [filter:drop-shadow(0_4px_6px_rgba(59,130,246,0.3))]" />,
            href: '/admin/portal-service',
            labelType: ['System'],
            hasAccess: true,
            hasAdminAccess: true,
            isLoading: false,
            health: generateHealth(),
            isServiceFolder: false, // System 서비스
          },
          {
            id: 'admin-tenants',
            label: 'Tenant 관리',
            description: '테넌트 생성, 수정 및 삭제',
            icon: <Building2 className="w-12 h-12 text-primary [filter:drop-shadow(0_4px_6px_rgba(251,146,60,0.3))]" />,
            href: '/admin/tenants',
            labelType: ['System'],
            hasAccess: true,
            hasAdminAccess: true,
            isLoading: false,
            health: generateHealth(),
            isServiceFolder: false, // System 서비스
          },
          {
            id: 'admin-data-storage',
            label: '데이터 저장소',
            description: 'Local/Cloud 저장 설정 및 데이터 동기화',
            icon: <HardDrive className="w-12 h-12 text-primary [filter:drop-shadow(0_4px_6px_rgba(34,211,238,0.3))]" />,
            href: '/admin/data-storage',
            labelType: ['System'],
            hasAccess: true,
            hasAdminAccess: true,
            isLoading: false,
            health: generateHealth(),
            isServiceFolder: false, // System 서비스
          },
        ];
        setAllServices(adminServices);
        setIsLoading(false);
        return;
      }

      // 서비스 목록이 아직 없으면 대기
      if (serviceConfigs.length === 0) {
        setIsLoading(false);
        return;
      }

      // Tenant 사용자인 경우: 권한 체크 전에 메뉴(accessibleServices)를 비워 서비스 카드와 동기화
      if (user?.userType === 'tenant-user') {
        setAccessibleServices([]);
      }

      // Tenant 사용자인 경우 admin 서비스 제외
      const filteredConfigs = user?.userType === 'tenant-user'
        ? serviceConfigs.filter(s => s.folder !== 'admin')
        : serviceConfigs;

      // 서비스 카드 매핑 (getServiceUrl: dev=localhost:port/{path}, prod=같은 오리진 경로)
      // 로컬·운영 공통: FE `/{folder}/`, Admin `/{folder}-admin/` (Vite base 와 일치)
      const mappedServices: ServiceCardData[] = filteredConfigs.map((service) => {
        const pf = service.ports.frontend;
        let href = getServiceUrl(pf, `/${service.folder}/`);

        // Special handling (Portal 기본 서비스만)
        if (service.folder === 'iampam-cloudwizard') href = getServiceUrl(pf, '/cloudwizard/');
        if (service.folder === 'asset-vulnerability') href = getServiceUrl(pf, '/vulnerability/');
        if (service.folder === 'iampam-vulnerability-manager') href = getServiceUrl(pf, '/vulnerability/');
        if (service.folder === 'it-service-request') href = '/it-service';
        if (service.folder === 'tenant-admin') href = '/tenant-admin';
        if (service.folder === 'admin') href = '/admin';

        let adminHref: string | undefined;
        if (service.adminRoute) {
          adminHref = service.adminRoute;
          // IT 서비스 요청: Workflow 설정 진입을 위해 Workflow 설정 페이지로 이동
          if (service.folder === 'it-service-request') adminHref = '/workflow-settings';
        } else if (service.ports['frontend-admin']) {
          const pa = service.ports['frontend-admin'];
          if (service.folder === 'iampam-cloudwizard') adminHref = getServiceUrl(pa, '/cloudwizard/');
          else if (service.folder === 'asset-vulnerability') adminHref = getServiceUrl(pa, '/vulnerability-admin/');
          else if (service.folder === 'iampam-vulnerability-manager') adminHref = getServiceUrl(pa, '/vulnerability-admin/');
          else adminHref = getServiceUrl(pa, `/${service.folder}-admin/`);
        }

        // Portal 기본 서비스 목록
        const PORTAL_CORE_SERVICES = ['it-service-request', 'tenant-admin', 'admin'];
        // services 폴더 안의 서비스인지 확인 (Portal 기본 서비스가 아니고, 'Service' 라벨이 있는 경우)
        const isServiceFolder = !PORTAL_CORE_SERVICES.includes(service.folder) && 
                                Array.isArray(service.labels) && 
                                service.labels.includes('Service');

        return {
          id: service.folder,
          label: service.name,
          description: service.description,
          icon: renderIcon(service.icon),
          iconName: service.icon,
          href,
          labelType: Array.isArray(service.labels) ? service.labels : [],
          adminHref,
          hasAccess: true,
          hasAdminAccess: true,
          isLoading: false,
          health: generateHealth(),
          isServiceFolder, // services 폴더 안의 서비스인지 여부
        };
      });

      // Tenant 사용자인 경우 권한 체크
      if (user?.userType === 'tenant-user' && user.tenantId) {
        try {
          const checks = [
            ...mappedServices.map(s => ({ serviceId: s.id, action: 'view' })),
            { serviceId: 'admin_service', action: 'service_management' },
          ];
          
          const batch = await checkPermissionBatch(user.tenantId, checks);
          const results = batch.results ?? [];
          if (!results.length && checks.length > 0) {
            console.warn('checkPermissionBatch returned no results; showing services without authz filter');
            setAllServices(mappedServices);
            setAccessibleServices(
              mappedServices.map((s) => ({ id: s.id, label: s.label, href: s.href, iconName: s.iconName, labelType: s.labelType })),
            );
            setIsLoading(false);
            return;
          }
          const permMap = new Map(results.map((r) => [`${r.serviceId}:${r.action}`, r.allowed]));
          const menuMap = new Map(results.map((r) => [r.serviceId, r.menuVisible !== false]));
          const adminAllowed = permMap.get('admin_service:service_management') ?? false;
          
          const servicesWithPermissions = mappedServices.map(service => {
            const hasView = permMap.get(`${service.id}:view`) ?? false;
            const menuVisible = menuMap.get(service.id) ?? true;
            const hasAccess = hasView && menuVisible;
            let hasAdminAccess: boolean;
            if (service.id === 'it-service-request') {
              // IT 서비스 요청: Workflow 설정(전용 UI) 진입
              hasAdminAccess = hasAccess;
            } else if (service.adminHref?.startsWith('/admin')) {
              hasAdminAccess = service.adminHref === '/admin?tab=service' ? adminAllowed : false;
            } else {
              hasAdminAccess = true;
            }
            // isServiceFolder 속성 유지
            return { ...service, hasAccess, hasAdminAccess, isServiceFolder: service.isServiceFolder };
          });
          
          setAllServices(servicesWithPermissions.filter((s) => s.hasAccess));
          setAccessibleServices(
            servicesWithPermissions
              .filter(s => s.hasAccess)
              .map(s => ({ id: s.id, label: s.label, href: s.href, iconName: s.iconName, labelType: s.labelType }))
          );
        } catch (error) {
          console.error('Failed to check permissions:', error);
          const noAccess = mappedServices.map(s => ({ ...s, hasAccess: false, hasAdminAccess: false }));
          setAllServices(noAccess);
          setAccessibleServices([]);
        }
      } else {
        // 비로그인 또는 기타
        setAllServices(mappedServices);
        if ((user as any)?.userType !== 'portal-admin') {
          setAccessibleServices([]);
        }
      }
      
      setIsLoading(false);
    };

    processServices();
  }, [serviceConfigs, user?.userType, (user as any)?.tenantId, setAccessibleServices, location.pathname, menuRefreshTick]);

  const localizedServices = useMemo(
    () =>
      allServices.map((service) => ({
        ...service,
        label: resolveServiceName(service.id, service.label, tServices),
        description: resolveServiceDescription(service.id, service.description, tServices),
      })),
    [allServices, tServices, i18n.language],
  );

  const handleServiceClick = useCallback((service: ServiceCardData) => {
    // 권한이 없거나 로딩 중인 경우 클릭 무시
    if (service.hasAccess === false || service.isLoading) {
      return;
    }

    const href = service.href;
    const isPortalInternalPath =
      href.startsWith('/entry') ||
      href.startsWith('/admin') ||
      href.startsWith('/it-service') ||
      href.startsWith('/tenant-admin') ||
      href.startsWith('/workflow-settings') ||
      href === '/login' ||
      href === '/';

    // 서비스 앱 경로는 전체 이동해야 Nginx가 해당 앱으로 라우팅한다.
    if (href.startsWith('http') || !isPortalInternalPath) {
      const { navigateToService } = useAuthStore.getState();
      navigateToService(href);
    } else {
      // 내부 라우트인 경우 TanStack Router 사용
      // 쿼리 파라미터 분리
      const [pathname, queryString] = href.split('?');
      if (queryString) {
        const search = Object.fromEntries(new URLSearchParams(queryString));
        navigate({ to: pathname as any, search: search as any });
      } else {
        navigate({ to: href });
      }
    }
  }, [navigate]);

  const handleAdminClick = (e: React.MouseEvent, adminHref: string, service: ServiceCardData) => {
    e.stopPropagation();

    if (service.hasAdminAccess === false) {
      return;
    }

    const isPortalInternalPath =
      adminHref.startsWith('/entry') ||
      adminHref.startsWith('/admin') ||
      adminHref.startsWith('/it-service') ||
      adminHref.startsWith('/tenant-admin') ||
      adminHref.startsWith('/workflow-settings') ||
      adminHref === '/login' ||
      adminHref === '/';

    if (adminHref.startsWith('http') || !isPortalInternalPath) {
      const { navigateToService } = useAuthStore.getState();
      navigateToService(adminHref);
    } else {
      // 내부 라우트인 경우 TanStack Router 사용
      // 쿼리 파라미터 분리
      const [pathname, queryString] = adminHref.split('?');
      if (queryString) {
        const search = Object.fromEntries(new URLSearchParams(queryString));
        navigate({ to: pathname as any, search: search as any });
      } else {
        navigate({ to: adminHref });
      }
    }

  };

  // Filter services based on search query and sort by label
  const filteredServices = useMemo(() => {
    let services = localizedServices;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      services = localizedServices.filter((service) => {
      const nameMatch = service.label.toLowerCase().includes(query);
      const descriptionMatch = service.description.toLowerCase().includes(query);
      const labelMatch = service.labelType.some((label) => label.toLowerCase().includes(query));
      return nameMatch || descriptionMatch || labelMatch;
      });
    }

    // API에서 displayOrder 기준으로 이미 정렬되어 옴, 순서 유지
    return services;
  }, [localizedServices, searchQuery]);

  // 엔터 입력 시 첫 번째 매칭 서비스로 이동
  useEffect(() => {
    const handleSearchSubmit = () => {
      if (filteredServices.length > 0 && searchQuery.trim()) {
        const firstService = filteredServices[0];
        // 권한이 있는 서비스만 이동
        if (firstService.hasAccess !== false && !firstService.isLoading) {
          handleServiceClick(firstService);
        }
      }
    };

    setOnSearchSubmit(() => handleSearchSubmit);
  }, [filteredServices, searchQuery, setOnSearchSubmit, handleServiceClick]);

  if (isLoading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{tLayout('loading')}</div>
        </div>
      
    );
  }

  return (
    
      <div className="space-y-6 pt-6">
        {servicesLoadError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm" role="alert">
            {servicesLoadError}
          </div>
        )}
        {/* Service Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredServices.length === 0 ? (
            <div className="col-span-full flex items-center justify-center py-12">
              <p className="text-muted-foreground">{tLayout('noSearchResults')}</p>
            </div>
          ) : (
            filteredServices.map((service) => (
              <ServiceCard
                key={service.id}
                id={service.id}
                label={service.label}
                description={service.description}
                icon={service.icon}
                labelType={service.labelType}
                hasAccess={service.hasAccess}
                hasAdminAccess={service.hasAdminAccess}
                isLoading={service.isLoading}
                adminHref={service.adminHref}
                href={service.href}
                health={service.health}
                isServiceFolder={service.isServiceFolder}
                onClick={() => handleServiceClick(service)}
                onAdminClick={service.adminHref ? (e) => handleAdminClick(e, service.adminHref!, service) : undefined}
              />
            ))
          )}
        </div>
      </div>
    
  );
};

EntryPageContent.displayName = 'EntryPageContent';

export const EntryPage: React.FC = () => {
  return (
    <SearchProvider>
      <EntryPageContent />
    </SearchProvider>
  );
};

EntryPage.displayName = 'EntryPage';
